import React from 'react';
import styled from 'styled-components';
import { useLanguage } from '../i18n/LanguageContext';

const DocumentsContainer = styled.div`
  padding: 100px 0;
  background: #ffffff;
  min-height: 100vh;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 30px;
`;

const PageTitle = styled.h1`
  text-align: center;
  font-size: 3.5rem;
  color: #2c3e50;
  margin-bottom: 25px;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const PageSubtitle = styled.p`
  text-align: center;
  font-size: 1.3rem;
  color: #7f8c8d;
  margin-bottom: 80px;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const DocumentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 40px;
  margin-bottom: 80px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const DocumentCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 40px 35px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
  text-align: center;
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
    border-color: #3498db;
  }
  
  .icon {
    font-size: 3.5rem;
    color: #3498db;
    margin-bottom: 25px;
  }
  
  h3 {
    color: #2c3e50;
    font-size: 1.6rem;
    margin-bottom: 20px;
    font-weight: 600;
  }
  
  p {
    color: #7f8c8d;
    line-height: 1.7;
    margin-bottom: 25px;
    font-size: 1.1rem;
  }
  
  .download-btn {
    background: #3498db;
    color: white;
    padding: 15px 30px;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 1rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    
    &:hover {
      background: #2980b9;
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(52, 152, 219, 0.3);
    }
  }
`;

const CertificatesSection = styled.section`
  background: #f8f9fa;
  padding: 80px 60px;
  border-radius: 12px;
  margin: 80px 0;
  border: 1px solid #e9ecef;
  
  @media (max-width: 768px) {
    padding: 60px 30px;
  }
  
  h2 {
    text-align: center;
    font-size: 2.8rem;
    color: #2c3e50;
    margin-bottom: 50px;
    font-weight: 600;
  }
`;

const CertificatesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 40px;
`;

const CertificateCard = styled.div`
  background: white;
  padding: 40px 30px;
  border-radius: 12px;
  text-align: center;
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);
    border-color: #3498db;
  }
  
  .cert-icon {
    font-size: 3rem;
    color: #3498db;
    margin-bottom: 20px;
  }
  
  h4 {
    color: #2c3e50;
    margin-bottom: 15px;
    font-size: 1.3rem;
    font-weight: 600;
  }
  
  p {
    color: #7f8c8d;
    font-size: 1rem;
    line-height: 1.6;
  }
`;

const Documents = () => {
  const { t } = useLanguage();
  const documents = [
    {
      id: 1,
      title: t('documents.qualityCert'),
      description: t('documents.qualityCertDesc'),
      icon: "🏆",
      type: "PDF"
    },
    {
      id: 2,
      title: t('documents.envCert'),
      description: t('documents.envCertDesc'),
      icon: "🌱",
      type: "PDF"
    },
    {
      id: 3,
      title: t('documents.safetyCert'),
      description: t('documents.safetyCertDesc'),
      icon: "🛡️",
      type: "PDF"
    },
    {
      id: 4,
      title: t('documents.techSpec'),
      description: t('documents.techSpecDesc'),
      icon: "📋",
      type: "PDF"
    },
    {
      id: 5,
      title: t('documents.testReports'),
      description: t('documents.testReportsDesc'),
      icon: "🔬",
      type: "PDF"
    },
    {
      id: 6,
      title: t('documents.prodPermits'),
      description: t('documents.prodPermitsDesc'),
      icon: "📜",
      type: "PDF"
    }
  ];

  const certificates = [
    {
      id: 1,
      title: "ISO 9001:2015",
      description: t('documents.qualityMgmt'),
      icon: "🏆"
    },
    {
      id: 2,
      title: "ISO 14001:2015",
      description: t('documents.envMgmt'),
      icon: "🌱"
    },
    {
      id: 3,
      title: "OHSAS 18001",
      description: t('documents.ohsMgmt'),
      icon: "🛡️"
    },
    {
      id: 4,
      title: "TS EN 197-1",
      description: t('documents.cementStd'),
      icon: "📊"
    },
    {
      id: 5,
      title: "CE Belgesi",
      description: t('documents.ceDoc'),
      icon: "🇪🇺"
    },
    {
      id: 6,
      title: "TSE Belgesi",
      description: t('documents.tseDoc'),
      icon: "🇹🇷"
    }
  ];

  return (
    <DocumentsContainer>
      <Container>
        <PageTitle>{t('documents.title')}</PageTitle>
        <PageSubtitle>
          {t('documents.subtitle')}
        </PageSubtitle>

        <DocumentsGrid>
          {documents.map(doc => (
            <DocumentCard key={doc.id}>
              <div className="icon">{doc.icon}</div>
              <h3>{doc.title}</h3>
              <p>{doc.description}</p>
              <button className="download-btn">
                {t('documents.download')} ({doc.type})
              </button>
            </DocumentCard>
          ))}
        </DocumentsGrid>

        <CertificatesSection>
          <h2>{t('documents.certificatesTitle')}</h2>
          <CertificatesGrid>
            {certificates.map(cert => (
              <CertificateCard key={cert.id}>
                <div className="cert-icon">{cert.icon}</div>
                <h4>{cert.title}</h4>
                <p>{cert.description}</p>
              </CertificateCard>
            ))}
          </CertificatesGrid>
        </CertificatesSection>
      </Container>
    </DocumentsContainer>
  );
};

export default Documents;